import { z } from '@repo/schema'
import { getDataWrapperSchema } from '@repo/schema/data-wrapper'
import { GetPaginationSchema } from '@repo/schema/pagination'
import {
  GetDefaultTableConfigurationSchema,
  GetSavedViewAudienceEntrySchema,
  GetSavedViewDetailSchema,
  GetSavedViewRecoveryItemSchema,
  GetSavedViewSummarySchema,
  StoredTableConfigurationSchema,
  TableConfigurationAudienceModeSchema,
  TableConfigurationAudienceSummarySchema,
} from '@repo/schema/table-configuration'
import { UpFetch } from '@repo/upfetch'

import { createProcedure } from '../../lib/procedure.ts'

const TablePathSchema = z.object({ tableKey: z.string() })
const SavedViewPathSchema = z.object({ id: z.number(), tableKey: z.string() })

const ListInputSchema = z.object({
  page: z.number().optional(),
  perPage: z.number().optional(),
  search: z.string().optional(),
})

const VersionedConfigurationSchema = z.object({
  configuration: StoredTableConfigurationSchema,
  schema_version: z.number(),
})

const OrganisationApplicabilitySchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('all') }),
  z.object({ organisation_ids: z.array(z.number()), type: z.literal('selected') }),
])

const SavedViewMetadataSchema = z.object({
  description: z.string().nullable().optional(),
  icon: z.string().nullable().optional(),
  name: z.string(),
  organisation_applicability: OrganisationApplicabilitySchema.optional(),
})

export const SavedViewsAccessSchema = z.enum(['all', 'owned', 'shared', 'pinned'])

export const SavedViewsInputSchema = ListInputSchema.extend({
  access: SavedViewsAccessSchema.optional(),
})

export const CreateSavedViewInputSchema = SavedViewMetadataSchema.extend({
  audience: z
    .object({
      mode: TableConfigurationAudienceModeSchema,
      role_ids: z.array(z.number()).optional(),
      user_ids: z.array(z.number()).optional(),
    })
    .optional(),
  configuration: StoredTableConfigurationSchema,
  schema_version: z.number(),
})

export const UpdateSavedViewInputSchema = SavedViewMetadataSchema.partial().extend({
  configuration: StoredTableConfigurationSchema.optional(),
  schema_version: z.number().optional(),
})

export const UpdateSavedViewAudienceInputSchema = z.object({
  add_role_ids: z.array(z.number()).optional(),
  add_user_ids: z.array(z.number()).optional(),
  mode: TableConfigurationAudienceModeSchema,
  remove_role_ids: z.array(z.number()).optional(),
  remove_user_ids: z.array(z.number()).optional(),
})

export type CreateSavedViewInput = z.input<typeof CreateSavedViewInputSchema>
export type SavedViewsAccess = z.infer<typeof SavedViewsAccessSchema>
export type SavedViewsInput = z.input<typeof SavedViewsInputSchema>
export type UpdateSavedViewAudienceInput = z.input<typeof UpdateSavedViewAudienceInputSchema>
export type UpdateSavedViewInput = z.input<typeof UpdateSavedViewInputSchema>

export const createTableConfigurationsRoute = (upFetch: UpFetch) => {
  const p = createProcedure(upFetch)

  return {
    audience: p
      .path(SavedViewPathSchema, p => [
        'table-configurations',
        p.tableKey,
        'saved-views',
        p.id,
        'audience',
      ])
      .input(
        ListInputSchema.omit({ page: true }).extend({
          type: z.enum(['all', 'user', 'role']).optional(),
        }),
      )
      .infiniteQuery(
        ({ input, page, path, upFetch }) =>
          upFetch(path, {
            params: { ...input, page },
            schema: GetPaginationSchema(GetSavedViewAudienceEntrySchema).extend({
              audience_summary: TableConfigurationAudienceSummarySchema,
            }),
          }),
        lastPage =>
          lastPage.meta.current_page < lastPage.meta.last_page
            ? lastPage.meta.current_page + 1
            : undefined,
      ),
    createSavedView: p
      .path(TablePathSchema, p => ['table-configurations', p.tableKey, 'saved-views'])
      .input(CreateSavedViewInputSchema)
      .mutation(({ input, path, upFetch }) =>
        upFetch(path, {
          body: input,
          method: 'POST',
          schema: getDataWrapperSchema(GetSavedViewDetailSchema),
        }),
      ),
    default: p
      .path(TablePathSchema, p => ['table-configurations', p.tableKey, 'default'])
      .query(({ path, upFetch }) =>
        upFetch(path, {
          // A User without a stored Default gets `{ data: null }`.
          schema: z
            .object({ data: GetDefaultTableConfigurationSchema.nullable() })
            .transform(result => result.data),
        }),
      ),
    deleteSavedView: p
      .path(SavedViewPathSchema, p => ['table-configurations', p.tableKey, 'saved-views', p.id])
      .mutation(({ path, upFetch }) => upFetch(path, { method: 'DELETE' })),
    pin: p
      .path(SavedViewPathSchema, p => [
        'table-configurations',
        p.tableKey,
        'saved-views',
        p.id,
        'pin',
      ])
      .mutation(({ path, upFetch }) => upFetch(path, { method: 'PUT' })),
    recovery: {
      delete: p
        .path(SavedViewPathSchema, p => [
          'table-configurations',
          p.tableKey,
          'recovery',
          'saved-views',
          p.id,
        ])
        .input(z.object({ reason: z.string() }))
        .mutation(({ input, path, upFetch }) => upFetch(path, { body: input, method: 'DELETE' })),
      savedViews: p
        .path(TablePathSchema, p => ['table-configurations', p.tableKey, 'recovery', 'saved-views'])
        .input(ListInputSchema)
        .query(({ input, path, upFetch }) =>
          upFetch(path, {
            params: input,
            schema: GetPaginationSchema(GetSavedViewRecoveryItemSchema),
          }),
        ),
      transfer: p
        .path(SavedViewPathSchema, p => [
          'table-configurations',
          p.tableKey,
          'recovery',
          'saved-views',
          p.id,
          'transfer',
        ])
        .input(z.object({ new_owner_user_id: z.number(), reason: z.string() }))
        .mutation(({ input, path, upFetch }) =>
          upFetch(path, {
            body: input,
            method: 'POST',
            schema: getDataWrapperSchema(GetSavedViewRecoveryItemSchema),
          }),
        ),
    },
    /** Listed pins take the slots they hold now, in this order; unlisted pins keep theirs. */
    reorderPins: p
      .path(TablePathSchema, p => ['table-configurations', p.tableKey, 'pins'])
      .input(z.object({ saved_view_ids: z.array(z.number()) }))
      .mutation(({ input, path, upFetch }) => upFetch(path, { body: input, method: 'PUT' })),
    savedView: p
      .path(SavedViewPathSchema, p => ['table-configurations', p.tableKey, 'saved-views', p.id])
      .query(({ path, upFetch }) =>
        upFetch(path, { schema: getDataWrapperSchema(GetSavedViewDetailSchema) }),
      ),
    savedViews: p
      .path(TablePathSchema, p => ['table-configurations', p.tableKey, 'saved-views'])
      .input(SavedViewsInputSchema)
      .query(({ input, path, upFetch }) =>
        upFetch(path, { params: input, schema: GetPaginationSchema(GetSavedViewSummarySchema) }),
      ),
    /**
     * Paged summaries for one picker section. Shares its URL and key prefix with `savedViews`;
     * the controller reads `savedViews` only with `access: 'pinned'`, so the inputs never collide.
     */
    savedViewsPages: p
      .path(TablePathSchema, p => ['table-configurations', p.tableKey, 'saved-views'])
      .input(SavedViewsInputSchema.omit({ page: true }))
      .infiniteQuery(
        ({ input, page, path, upFetch }) =>
          upFetch(path, {
            params: { ...input, page },
            schema: GetPaginationSchema(GetSavedViewSummarySchema),
          }),
        lastPage =>
          lastPage.meta.current_page < lastPage.meta.last_page
            ? lastPage.meta.current_page + 1
            : undefined,
      ),
    transferSavedView: p
      .path(SavedViewPathSchema, p => [
        'table-configurations',
        p.tableKey,
        'saved-views',
        p.id,
        'transfer',
      ])
      .input(z.object({ new_owner_user_id: z.number() }))
      .mutation(({ input, path, upFetch }) =>
        upFetch(path, {
          body: input,
          method: 'POST',
          schema: getDataWrapperSchema(GetSavedViewDetailSchema),
        }),
      ),
    unpin: p
      .path(SavedViewPathSchema, p => [
        'table-configurations',
        p.tableKey,
        'saved-views',
        p.id,
        'pin',
      ])
      .mutation(({ path, upFetch }) => upFetch(path, { method: 'DELETE' })),
    updateAudience: p
      .path(SavedViewPathSchema, p => [
        'table-configurations',
        p.tableKey,
        'saved-views',
        p.id,
        'audience',
      ])
      .input(UpdateSavedViewAudienceInputSchema)
      .mutation(({ input, path, upFetch }) =>
        upFetch(path, {
          body: input,
          method: 'PATCH',
          schema: getDataWrapperSchema(GetSavedViewDetailSchema),
        }),
      ),
    updateDefault: p
      .path(TablePathSchema, p => ['table-configurations', p.tableKey, 'default'])
      .input(VersionedConfigurationSchema)
      .mutation(({ input, path, upFetch }) =>
        upFetch(path, {
          body: input,
          method: 'PUT',
          schema: getDataWrapperSchema(GetDefaultTableConfigurationSchema),
        }),
      ),
    updateSavedView: p
      .path(SavedViewPathSchema, p => ['table-configurations', p.tableKey, 'saved-views', p.id])
      .input(UpdateSavedViewInputSchema)
      .mutation(({ input, path, upFetch }) =>
        upFetch(path, {
          body: input,
          method: 'PATCH',
          schema: getDataWrapperSchema(GetSavedViewDetailSchema),
        }),
      ),
  }
}
